import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useOrganisme } from '../context/OrganismeContext';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import FileDropzone from '../components/common/FileDropzone';

const VISIBILITES_VALIDES = ['public', 'tous', 'organisme', 'SAR', 'AMAIR', 'ADAR'];

/** Même principe que l’enregistrement de la cover événement : on envoie seulement s3Key + originalName, pas l’URL signée. */
function toImagePrincipalePayload(ip) {
  if (ip == null || ip === '') return null;
  if (typeof ip === 'string') return ip;
  if (ip.s3Key) {
    return { s3Key: ip.s3Key, originalName: ip.originalName || null };
  }
  if (ip.legacyPath) return ip.legacyPath;
  return null;
}

export default function AdminBlogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organismeActif } = useOrganisme();
  const hasAdminRole =
    user &&
    (user.roles || [user.role]).some((r) => ['admin', 'super_admin'].includes(r));
  const isSuperAdminUser =
    user && (user.roles || [user.role]).includes('super_admin');
  const editorRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    titre: '',
    contenu: '',
    extrait: '',
    visibilite: '',
    statut: 'brouillon',
    datePublication: '',
    imagePrincipale: null,
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [originalStatut, setOriginalStatut] = useState(null);

  useEffect(() => {
    if (!hasAdminRole) {
      if (user) navigate('/');
      return;
    }
    if (id) {
      fetchArticle();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, navigate, hasAdminRole]);

  const organismeForPayload = () => {
    const orgs = user?.organismes || [];
    if (organismeActif && orgs.includes(organismeActif)) return organismeActif;
    if (orgs.length > 0) return orgs[0];
    return user?.organisme || null;
  };

  const fetchArticle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/actualites/articles/admin/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const article = response.data;
      if (article) {
        setOriginalStatut(article.statut);
        setFormData({
          titre: article.titre,
          contenu: article.contenu,
          extrait: article.extrait || '',
          visibilite: article.visibilite || '',
          statut: article.statut,
          datePublication: article.datePublication ? new Date(article.datePublication).toISOString().slice(0, 16) : '',
          imagePrincipale: article.imagePrincipale || null,
          tags: article.tags || []
        });
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'article:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (content) => {
    setFormData(prev => ({ ...prev, contenu: content }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/actualites/upload-image`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setFormData((prev) => ({
        ...prev,
        imagePrincipale: {
          s3Key: response.data.s3Key,
          originalName: response.data.originalName || null,
          url: response.data.url,
        },
      }));
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
    }
  };

  const handleTakeOffline = async (e) => {
    e.preventDefault();
    
    if (!window.confirm('Voulez-vous vraiment mettre cet article hors ligne ? Il ne sera plus visible par les adhérents et repassera en brouillon.')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        statut: 'brouillon',
        imagePrincipale: toImagePrincipalePayload(formData.imagePrincipale),
      };

      await axios.put(
        `${process.env.REACT_APP_API_URL}/actualites/articles/${id}`,
        dataToSend,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      navigate('/admin/actualites');
    } catch (error) {
      console.error('Erreur lors de la mise hors ligne:', error);
      alert(error.response?.data?.message || 'Erreur lors de la mise hors ligne');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e, action = 'save') => {
    e.preventDefault();
    setLoading(true);

    try {
      const v = formData.visibilite?.trim();
      if (!v || !VISIBILITES_VALIDES.includes(v)) {
        alert('Veuillez sélectionner une visibilité pour l\'article.');
        setLoading(false);
        return;
      }

      const org = organismeForPayload();
      if (!org) {
        alert('Aucun organisme n\'est associé à votre session. Sélectionnez un organisme ou contactez un administrateur.');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        visibilite: v,
        organisme: org,
        statut: action === 'publish' ? 'publie' : action === 'schedule' ? 'programme' : formData.statut,
        imagePrincipale: toImagePrincipalePayload(formData.imagePrincipale),
      };

      if (action === 'schedule' && !dataToSend.datePublication) {
        alert('Veuillez sélectionner une date de publication');
        setLoading(false);
        return;
      }

      if (id) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/actualites/articles/${id}`,
          dataToSend,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/actualites/articles`,
          dataToSend,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      navigate('/admin/actualites');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate('/admin/actualites')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour à la gestion
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Modifier l\'article' : 'Nouvel article'}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={(e) => handleSubmit(e, 'save')} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Titre */}
              <div className="bg-white rounded-lg shadow p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre *
                </label>
                <input
                  type="text"
                  name="titre"
                  value={formData.titre}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Titre de l'article"
                />
              </div>

              {/* Extrait */}
              <div className="bg-white rounded-lg shadow p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Extrait
                </label>
                <textarea
                  name="extrait"
                  value={formData.extrait}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Court résumé de l'article (optionnel)"
                  maxLength={300}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.extrait.length}/300 caractères
                </p>
              </div>

              {/* Contenu */}
              <div className="bg-white rounded-lg shadow p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contenu *
                </label>
                <Editor
                  apiKey="yz5jdxbkp4i9v4omefqdsmbc6pxaqmyor9p6sh5cfi2f3gz5"
                  onInit={(evt, editor) => editorRef.current = editor}
                  value={formData.contenu}
                  onEditorChange={handleEditorChange}
                  init={{
                    height: 500,
                    menubar: true,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                      'preview', 'anchor', 'searchreplace', 'visualblocks', 'code',
                      'fullscreen', 'insertdatetime', 'media', 'table', 'help', 'wordcount'
                    ],
                    toolbar: 'undo redo | formatselect | bold italic underline strikethrough | ' +
                      'alignleft aligncenter alignright alignjustify | ' +
                      'bullist numlist outdent indent | link image | ' +
                      'forecolor backcolor | removeformat | help',
                    content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px }',
                    images_upload_url: `${process.env.REACT_APP_API_URL}/actualites/upload-image`,
                    images_upload_handler: async (blobInfo, progress) => {
                      const formData = new FormData();
                      formData.append('file', blobInfo.blob(), blobInfo.filename());
                      
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.post(
                          `${process.env.REACT_APP_API_URL}/actualites/upload-image`,
                          formData,
                          {
                            headers: {
                              Authorization: `Bearer ${token}`,
                              'Content-Type': 'multipart/form-data'
                            }
                          }
                        );
                        return response.data.url;
                      } catch (error) {
                        throw new Error('Upload failed');
                      }
                    },
                  }}
                />
              </div>
            </div>
            {/* Sidebar */}
            <div className="space-y-6">
              {/* Actions */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Actions</h3>
                {id && originalStatut === 'publie' ? (
                  // Article déjà publié : options différentes
                  <div className="space-y-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Enregistrer les modifications
                    </button>
                    <button
                      type="button"
                      onClick={handleTakeOffline}
                      disabled={loading}
                      className="w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      Mettre hors ligne
                    </button>
                    <p className="mt-2 text-xs text-gray-500">
                      L'article est actuellement publié et visible.
                    </p>
                  </div>
                ) : (
                  // Nouvel article ou brouillon/programmé : options classiques
                  <div className="space-y-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 disabled:opacity-50"
                    >
                      Enregistrer brouillon
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleSubmit(e, 'schedule')}
                      disabled={loading}
                      className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Programmer
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleSubmit(e, 'publish')}
                      disabled={loading}
                      className="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      Publier maintenant
                    </button>
                  </div>
                )}
              </div>

              {/* Visibilité */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Visibilité *</h3>
                <div className="space-y-2">
                  <label className="flex items-start">
                    <input
                      type="radio"
                      name="visibilite"
                      value="public"
                      checked={formData.visibilite === 'public'}
                      onChange={handleChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 mt-0.5"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <span className="font-medium">Public</span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Visible par tout le monde, y compris sans compte sur la page Actualités
                      </span>
                    </span>
                  </label>
                  {/* Si super_admin ou admin multi-organismes */}
                  {(isSuperAdminUser || (user?.organismes && user.organismes.length > 1)) ? (
                    <>
                      <label className="flex items-start">
                        <input
                          type="radio"
                          name="visibilite"
                          value="organisme"
                          checked={formData.visibilite === 'organisme'}
                          onChange={handleChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 mt-0.5"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          <span className="font-medium">Adhérents de l&apos;organisme porteur</span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            Uniquement les adhérents ayant une adhésion à l&apos;organisme de l&apos;article
                            {organismeActif ? ` (${organismeActif})` : ''}
                          </span>
                        </span>
                      </label>
                      {/* Options pour chaque organisme */}
                      {(user?.organismes || []).map((org) => (
                        <label key={org} className="flex items-center">
                          <input
                            type="radio"
                            name="visibilite"
                            value={org}
                            checked={formData.visibilite === org}
                            onChange={handleChange}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Uniquement {org}
                          </span>
                        </label>
                      ))}
                      {/* Option tous les adhérents */}
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="visibilite"
                          value="tous"
                          checked={formData.visibilite === 'tous'}
                          onChange={handleChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Tous les adhérents ({(user?.organismes || []).join(' + ')})
                        </span>
                      </label>
                    </>
                  ) : (
                    /* Admin avec un seul organisme */
                    <>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="visibilite"
                          value="organisme"
                          checked={formData.visibilite === 'organisme'}
                          onChange={handleChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Uniquement {user?.organisme || (user?.organismes && user.organismes[0])}
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="visibilite"
                          value="tous"
                          checked={formData.visibilite === 'tous'}
                          onChange={handleChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Tous les adhérents
                        </span>
                      </label>
                    </>
                  )}
                </div>
              </div>

              {/* Date de publication */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Date de publication</h3>
                <input
                  type="datetime-local"
                  name="datePublication"
                  value={formData.datePublication}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Laissez vide pour publier immédiatement
                </p>
              </div>

              {/* Image principale */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Image principale</h3>
                {formData.imagePrincipale ? (
                  <div className="relative">
                    <img
                      src={
                        typeof formData.imagePrincipale === 'string'
                          ? `${process.env.REACT_APP_API_URL}${formData.imagePrincipale}`
                          : formData.imagePrincipale?.url ||
                            (formData.imagePrincipale?.legacyPath
                              ? `${process.env.REACT_APP_API_URL}${formData.imagePrincipale.legacyPath}`
                              : '')
                      }
                      alt="Preview"
                      className="w-full h-40 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, imagePrincipale: null }))}
                      className="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-xs rounded-md hover:bg-red-700"
                    >
                      Supprimer
                    </button>
                  </div>
                ) : (
                  <FileDropzone
                    onFileSelect={(file) => handleImageUpload({ target: { files: [file] } })}
                    label="Ajouter une image"
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                  />
                )}
              </div>

              {/* Tags */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Tags</h3>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    placeholder="Ajouter un tag"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
                  >
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-indigo-100 text-indigo-700"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
